import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { LogoBicolor } from '../components/LogoBicolor';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import './landing-design.css';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Login realizado com sucesso!');
      // Admins go straight to the admin panel — never the regular dashboard
      navigate(userData?.is_admin ? '/admin/dashboard' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="akali-landing min-h-screen relative flex items-center justify-center px-4 py-12">
      {/* Ambient glow — mirrors the LP hero */}
      <div className="lp-hero-glow" />

      <div className="relative z-10 max-w-md w-full">
        {/* Brand */}
        <div className="flex items-center justify-center mb-8">
          <LogoBicolor size={24} symbolSize={36} />
        </div>

        <p
          className="lp-mono lp-text-muted text-center mb-8"
          style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.15em' }}
          data-testid="brand-tagline"
        >
          Documentação clínica inteligente
        </p>

        {/* Form card — glassmorphism */}
        <div className="lp-card p-8">
          <h1 className="lp-display lp-text-primary text-center mb-1" style={{ fontSize: 28, fontWeight: 600 }}>
            Entrar
          </h1>
          <p className="lp-body lp-text-secondary text-center mb-8" style={{ fontSize: 15 }}>
            Acesse sua conta Akali AI
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 lp-body"
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-glass-border)',
                    color: 'var(--color-text-primary)',
                    height: 44,
                  }}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="lp-body lp-text-secondary" style={{ fontSize: 14 }}>Senha</Label>
              <PasswordInput
                id="password"
                leftIcon={Lock}
                iconColor="text-[color:var(--color-text-muted)]"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="lp-body"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-glass-border)',
                  color: 'var(--color-text-primary)',
                  height: 44,
                }}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
              />
            </div>

            <button
              type="submit"
              className="lp-btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Entrando...</>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              className="lp-body lp-text-secondary hover:lp-text-primary transition-colors"
              style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}
            >
              Esqueci minha senha
            </Link>
          </div>

          <div
            className="mt-6 text-center lp-body lp-text-muted"
            style={{ fontSize: 14, borderTop: '1px solid var(--color-glass-border)', paddingTop: 24 }}
          >
            Não tem uma conta?{' '}
            <Link
              to="/register"
              className="lp-text-accent hover:underline"
              style={{ fontWeight: 500 }}
            >
              Criar conta
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="lp-mono lp-text-muted hover:lp-text-primary transition-colors"
            style={{ fontSize: 12 }}
          >
            ← Voltar para a home
          </Link>
        </div>
      </div>
    </div>
  );
}
